/**
 * Shared animation constants and variants for consistent UI animations
 * Used across LoadingShim, EmptyState, and other animated components
 */

/**
 * Animation delay constants (in seconds)
 */
export const ANIMATION_DELAYS = {
  stagger: 0.05,        // Delay between list items
  messageStagger: 0.08,  // Delay between message bubbles (slightly longer for visual flow)
  short: 0.1,           // Quick delay for labels/headers
  medium: 0.2,          // Medium delay for content
  long: 0.3,            // Longer delay for final elements
}

/**
 * Animation duration constants (in seconds)
 */
export const ANIMATION_DURATIONS = {
  fast: 0.2,        // Quick transitions
  normal: 0.3,      // Standard transitions
  smooth: 0.4,      // Smooth, noticeable transitions
  emptyState: 0.5,  // Empty state animations (slightly longer for emphasis)
}

/**
 * Long-running animation durations (in seconds)
 */
export const LOOP_ANIMATION_DURATIONS = {
  questionFlow: 48,
}

/**
 * Common animation variants for loading states
 */
export const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: ANIMATION_DURATIONS.normal }
}

export const fadeUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: ANIMATION_DURATIONS.smooth }
}

export const fadeUpShort = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: ANIMATION_DURATIONS.normal }
}

export const slideInLeft = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: ANIMATION_DURATIONS.normal }
}

export const slideInLeftShort = {
  initial: { opacity: 0, x: -10 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: ANIMATION_DURATIONS.fast }
}

export const scaleIn = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: ANIMATION_DURATIONS.normal }
}

export const slideUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: ANIMATION_DURATIONS.smooth }
}

/**
 * Helper to create transition with custom delay
 * @param {Object} baseTransition - Base transition object
 * @param {number} delay - Delay in seconds
 * @returns {Object} Transition object with delay
 */
export const withDelay = (baseTransition, delay) => ({
  ...baseTransition,
  transition: { ...baseTransition.transition, delay }
})
